<template>
  <section class="account-cellar no-border-bottom">
    <ProductSavedSearch
      :selected-filters="selectedFilters"
      :show="saveSearchShowModal"
      :edit-search="savedSearchEditId"
      :compare-current-search="false"
      @saveComplete="cancelAddSearch"
    />
    <CellarHeader active-link="saved" />

    <div class="site-width site-width-full">
      <div class="account-cellar--my-favorites active">
        <div class="account-cellar--utilities row">
          <div>
            <div class="shopping columns small-12 medium-4 large-4">
              <nuxt-link v-if="wineries.length > 0" :to="favoriteWineriesURL">
                <span class="icon-shopping"></span>
                Shop My Wineries
              </nuxt-link>
            </div>
            <div class="add-button columns small-12 medium-8 large-8">
              <a href="#" @click.prevent="openWinerySearch">
                <span class="icon-green-add"></span>
                Add Winery
              </a>
              <a href="#" @click.prevent="addSavedSearch">
                <span class="icon-green-add"></span>
                Add Saved Search
              </a>
            </div>
          </div>
        </div>
        <div class="account-cellar--favorite-container">
          <div class="search-container" :class="{ active: showWinerySearch }">
            <div class="winery-search">
              <input
                id="winery-search-input"
                ref="winerySearchInput"
                v-model="winerySearchString"
                type="search"
              />
              <button
                v-if="winerySearchString"
                class="clear"
                @click="clearSearch"
              >
                <span class="sr-only">Clear Search</span>
              </button>
              <button class="close" @click="closeWinerySearch">Close</button>
              <div class="winery-search-results">
                <ul class="winery-search-results--list">
                  <li
                    v-for="wineryData in winerySearchResults"
                    :key="`search-${wineryData.id}`"
                    class="winery-search-results--list-item"
                  >
                    <span>
                      {{ wineryData.name }}
                    </span>
                    <a
                      href="#"
                      @click.prevent="showNotifyMeModal(wineryData.id)"
                    >
                      <span class="icon-plus"></span>
                      <span class="sr-only">Add</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="filter-container" :class="{ active: showAddSearch }">
            <div id="plp-filter-sidebar" class="plp-filter-sidebar">
              <div class="filter-scroller">
                <div class="search">
                  <label class="sr-only" for="new-saved-search">
                    Search Term
                  </label>
                  <input
                    id="new-saved-search"
                    ref="savedSearchInput"
                    v-model="searchString"
                    type="search"
                  />
                </div>
                <FilterAccordions
                  v-if="showAddSearch"
                  :categories="allCategoryFilters"
                  :varietals="allVarietalFilters"
                  :food-pairings="allFoodPairingFilters"
                  :vintages="vintageFilters"
                  :expert-reviewers="expertReviewerFilters"
                  :occasion-slugs="occasionSlugs"
                  :hide-extra-varietals="hideExtraVarietals"
                  :current-filters="selectedFilters"
                  :use-selected-filters="true"
                  @sliderChanged="sliderChanged"
                  @sliderCleared="sliderCleared"
                  @toggleHideExtraVarietals="toggleHideExtraVarietals"
                  @toggleFilter="toggleFilter"
                  @updateRegionFilters="updateRegionFilters"
                />
                <div class="plp-filter-footer">
                  <div class="row">
                    <div class="columns small-6">
                      <button @click="cancelAddSearch">Cancel</button>
                    </div>
                    <div class="columns small-6">
                      <button class="apply" @click="commitAddSearch">
                        Save and View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="account-cellar--favorite-content">
            <div class="favorite-list-group">
              <div
                v-if="savedSearchList.length === 0 && wineries.length === 0"
                class="empty"
              >
                Set up customized alerts by winery, region, rating, and vintage
                to be the first in line when new wines arrive.
              </div>
              <ul class="favorite-list">
                <li v-for="search in savedSearchList" :key="search.id">
                  <nuxt-link
                    :to="`/store/search/?${decodeURIComponent(
                      search.original_query_string,
                    )}`"
                    class="favorite-name"
                  >
                    {{ search.name }}
                  </nuxt-link>
                  <a href="#" @click.prevent="editSavedSearch(search)">
                    <span class="icon-pencil"></span>
                    <div class="sr-only">Edit</div>
                  </a>
                </li>
              </ul>
              <ul class="favorite-list">
                <li v-for="winery in wineries" :key="winery.winery_data.id">
                  <nuxt-link
                    :to="`/store/wineries/${winery.winery_data.slug}`"
                    class="favorite-name"
                  >
                    {{ winery.winery_data.name }}
                  </nuxt-link>
                  <a href="#" @click.prevent="removeFavorite(winery)">
                    <span class="icon-delete-circled"></span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <NotifyMeModal
      :visible="notifyMeModalOpen"
      :winery-id="notifyWineryId"
      @submitted="addWinery"
      @closed="hideNotifyMeModal"
    />
  </section>
</template>

<script setup>
import { deslugify } from '#common/utils/slugify'
import { pushEvent } from '#common/utils/user'
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { filterifyMetadata } from '~/utils/filters/filters'

const store = useStore()
const route = useRoute()
const { $auth, $axios, $moment } = useNuxtApp()
const { isLoading } = storeToRefs(useLayout())

$auth.requireLogin()

// Refs
const savedSearchInput = ref(null)
const winerySearchInput = ref(null)
const searchString = ref('')
const showAddSearch = ref(false)
const hideExtraVarietals = ref(true)
const selectedFilters = ref([])
const favorites = ref([])
const regionsLoading = ref(false)
const notifyMeModalOpen = ref(false)
const notifyWineryId = ref(null)
const winerySearchString = ref('')
const showWinerySearch = ref(false)
const winerySearchResults = ref([])
const metadata = useMetadata()

// Computed
const favoriteWineriesURL = computed(() => {
  if (wineries.value.length === 0) {
    return '/store/'
  } else if (wineries.value.length === 1) {
    return `/store/wineries/${wineries.value[0].winery_data.slug}/`
  }
  const winerySlugListString = wineries.value
    .map((fav) => fav.winery_data.slug)
    .join(',')
  return `/store/search/?wineries=${winerySlugListString}`
})

const wineries = computed(() => {
  const uniqueWineries = new Set()
  return favorites.value
    .filter(
      (fav) =>
        fav.is_active &&
        !uniqueWineries.has(fav.winery_data.id) &&
        uniqueWineries.add(fav.winery_data.id),
    )
    .sort((a, b) => a.winery_data.name.localeCompare(b.winery_data.name))
})

const allCategoryFilters = computed(() =>
  filterifyMetadata(metadata.categories, 'categories'),
)

const allVarietalFilters = computed(() => {
  const filters = filterifyMetadata(
    Object.values(metadata.varietals),
    'varietals',
  )
  if (hideExtraVarietals.value) {
    return filters.filter(
      (v) =>
        isFilterSelected(v) ||
        v.show_in_filter_dropdown ||
        !hideExtraVarietals.value,
    )
  }
  return filters
})

const vintageFilters = computed(() => {
  const currentYear = $moment().year()
  const fiftyYearsAgo = $moment().subtract(50, 'years').year()

  const vintages = []
  for (let year = fiftyYearsAgo; year <= currentYear; year++) {
    vintages.push({
      category: 'vintage',
      key: year.toString(),
      name: year.toString(),
    })
  }
  vintages.sort((a, b) => a.name.localeCompare(b.name))
  return vintages
})

const allFoodPairingFilters = computed(() =>
  filterifyMetadata(metadata.food, 'food_pairings'),
)

const expertReviewerFilters = computed(() =>
  filterifyMetadata(
    metadata.expert_reviewers.filter((expert) => expert.filterable),
    'expert_reviewers',
  ),
)

const occasionSlugs = computed(() => Object.keys(metadata.facetNames.occasions))

const savedSearchList = computed(() => store.getters.savedSearchList)
const saveSearchShowModal = computed(() => store.getters.saveSearchShowModal)
const savedSearchEditId = computed(() => store.getters.savedSearchEditId)

// Methods
const startSpinner = () => {
  isLoading.value = true
}

const stopSpinner = () => {
  isLoading.value = false
}

const addSavedSearch = () => {
  showAddSearch.value = true
  if (regionsLoading.value) {
    startSpinner()
  }
  nextTick(() => {
    savedSearchInput.value?.focus()
  })
}

const editSavedSearch = (search) => {
  store.dispatch('setSavedSearchModal', {
    editId: search.id,
    showModal: true,
  })
}

const openWinerySearch = () => {
  showWinerySearch.value = true
  nextTick(() => {
    winerySearchInput.value?.focus()
  })
}

const clearSearch = () => {
  winerySearchString.value = ''
}

const closeWinerySearch = () => {
  showWinerySearch.value = false
  winerySearchString.value = ''
}

const winerySearch = async () => {
  if (!winerySearchString.value) {
    winerySearchResults.value = []
    return
  }
  try {
    const results = await $axios.get(
      `/api/notifyme/winery/?search=${winerySearchString.value}`,
    )
    winerySearchResults.value = results.data.results.filter(
      (item) =>
        !favorites.value.some(
          (favorite) => favorite.winery === item.id && favorite.is_active,
        ),
    )
  } catch (e) {
    console.error('Error fetching the winery search results')
    throw e
  }
}

const showNotifyMeModal = (wineryId) => {
  notifyWineryId.value = wineryId
  notifyMeModalOpen.value = true
}

const hideNotifyMeModal = () => {
  notifyMeModalOpen.value = false
  notifyWineryId.value = null
}

const addWinery = (notification) => {
  const searchIndex = winerySearchResults.value.findIndex(
    (item) => item.id === notification.winery,
  )
  winerySearchResults.value.splice(searchIndex, 1)
  favorites.value.push(notification)
}

const removeFavorite = async (favorite) => {
  try {
    await $axios.delete(
      `/api/notifyme/cancel-winery-subscription/${favorite.winery_data.id}/`,
    )
    pushEvent(
      {
        metric_name: 'Notification Stop',
        url_path: route.fullPath,
        extra_data: {
          notificationID: favorite.id,
        },
      },
      store,
    )
    favorite.is_active = false
    await fetchData()
  } catch (e) {
    console.error(
      `Failed to remove subscription for notification ${favorite.id}`,
    )
    throw e
  }
}

const isFilterSelected = (filter) => {
  return !!selectedFilters.value.find((f) => {
    return f.category === filter.category && f.key === filter.key
  })
}

const cancelAddSearch = () => {
  showAddSearch.value = false
  searchString.value = ''
  selectedFilters.value = []
  metadata.deselectAllRegions()
}

const commitAddSearch = () => {
  selectedFilters.value = selectedFilters.value.filter(
    (f) => !['search'].includes(f.category),
  )
  const trimmedSearch = searchString.value.trim()
  if (trimmedSearch) {
    selectedFilters.value.push({
      category: 'search',
      key: trimmedSearch,
      name: trimmedSearch,
    })
  }

  store.dispatch('setSavedSearchModal', {
    editId: null,
    showModal: true,
  })
}

const toggleHideExtraVarietals = () => {
  hideExtraVarietals.value = !hideExtraVarietals.value
}

const toggleFilter = (e) => {
  if (e.category === 'region_slug') {
    const region = metadata.regionsIndex.find((r) => r.chipName === e.name)
    if (region) metadata.deselectRegion({ region })
    updateRegionFilters()
    return
  }
  const filterIndex = selectedFilters.value.findIndex(
    (f) => f.category === e.category && f.key === e.key,
  )
  if (filterIndex >= 0) {
    selectedFilters.value.splice(filterIndex, 1)
  } else {
    selectedFilters.value.push(e)
  }
}

const updateRegionFilters = () => {
  selectedFilters.value = selectedFilters.value
    .filter((f) => f.category !== 'region_slug')
    .concat(
      metadata.selectedRegionSlugs.map((slug) => {
        const slugIndex = metadata.regionsIndex.find(
          (region) => region.slug === slug,
        )
        return {
          category: 'region_slug',
          key: slug,
          slug,
          name: slugIndex ? slugIndex.name : deslugify(slug),
          chipName: slugIndex ? slugIndex.chipName : deslugify(slug),
        }
      }),
    )
}

const sliderChanged = (newFilter) => {
  selectedFilters.value = selectedFilters.value.filter(
    (f) => !(f.category === newFilter.category && f.key === newFilter.key),
  )
  selectedFilters.value.push(newFilter)
}

const sliderCleared = (clearedFilter) => {
  selectedFilters.value = selectedFilters.value.filter(
    (f) =>
      !(f.category === clearedFilter.category && f.key === clearedFilter.key),
  )
}

// Fetch data
const fetchData = async () => {
  if (!$auth.loggedIn) {
    favorites.value = []
    return
  }
  startSpinner()
  await store.dispatch('getSavedSearches')
  try {
    const result = await $axios.get('/api/notifyme/notification-subscription/')
    favorites.value = result.data.results
  } catch (e) {
    console.error(e)
  } finally {
    stopSpinner()
  }
}

// Watchers
watch(() => $auth.loggedIn, fetchData)
watch(winerySearchString, winerySearch)

// Initial fetch
onActivated(fetchData)
</script>
